import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        navigate('/dashboard');
      } else {
        setMessage(data.error || 'Errore durante il login');
      }
    } catch (error) {
      setMessage('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#1b1b1b',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  };

  const loginCardStyle = {
    backgroundColor: 'rgba(27, 27, 27, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    padding: '60px 50px',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '480px',
    border: '1px solid rgba(235, 205, 97, 0.15)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(235, 205, 97, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    animation: 'fadeInUp 0.6s ease-out',
    position: 'relative',
    zIndex: 1
  };

  const logoContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '50px',
    position: 'relative'
  };

  const logoStyle = {
    maxWidth: '180px',
    height: 'auto',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(235, 205, 97, 0.2)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    animation: 'fadeIn 0.8s ease-out'
  };

  const titleStyle = {
    textAlign: 'center',
    color: '#ebcd61',
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '12px',
    letterSpacing: '-0.5px',
    background: 'linear-gradient(135deg, #ebcd61 0%, #f5d876 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: 'fadeIn 0.8s ease-out 0.2s both'
  };

  const subtitleStyle = {
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '20px',
    marginBottom: '45px',
    fontWeight: '500',
    letterSpacing: '0.3px',
    animation: 'fadeIn 0.8s ease-out 0.3s both'
  };

  const inputContainerStyle = {
    marginBottom: '24px',
    position: 'relative'
  };

  const passwordToggleStyle = {
    position: 'absolute',
    right: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: 'rgba(235, 205, 97, 0.6)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    zIndex: 1,
    borderRadius: '6px',
    backgroundColor: 'rgba(235, 205, 97, 0.05)'
  };

  const inputStyle = {
    width: '100%',
    padding: '18px 20px',
    fontSize: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1.5px solid rgba(235, 205, 97, 0.2)',
    borderRadius: '12px',
    boxSizing: 'border-box',
    color: '#ffffff',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const inputFocusStyle = {
    border: '1.5px solid #ebcd61',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    boxShadow: '0 0 0 4px rgba(235, 205, 97, 0.15), 0 8px 24px rgba(235, 205, 97, 0.1)',
    transform: 'translateY(-2px)'
  };

  const buttonStyle = {
    width: '100%',
    padding: '18px',
    fontSize: '17px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #ebcd61 0%, #f5d876 100%)',
    color: '#1b1b1b',
    border: 'none',
    borderRadius: '12px',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    marginTop: '15px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    boxShadow: '0 4px 16px rgba(235, 205, 97, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  };

  const errorMessageStyle = {
    marginTop: '24px',
    padding: '16px 20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid rgba(220, 53, 69, 0.3)',
    color: '#ff6b6b',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '500',
    animation: 'shake 0.5s ease-in-out',
    backdropFilter: 'blur(10px)'
  };

  const [focusedInput, setFocusedInput] = useState(null);

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 4px 16px rgba(235, 205, 97, 0.3);
          }
          50% {
            box-shadow: 0 4px 24px rgba(235, 205, 97, 0.5);
          }
        }
        
        .login-container::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(235, 205, 97, 0.1) 0%, transparent 70%);
          animation: rotate 20s linear infinite;
          pointer-events: none;
        }
        
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
      <div style={containerStyle} className="login-container">
        <div style={loginCardStyle}>
        <div style={logoContainerStyle}>
          <img 
            src="/images/503547065_18084417088686417_5527300001175770798_n.jpg" 
            alt="Kinesis Fit Center Logo" 
            style={logoStyle}
            onError={(e) => {
              console.error('Errore nel caricamento del logo:', e);
              e.target.style.display = 'none';
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 12px 32px rgba(235, 205, 97, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 8px 24px rgba(235, 205, 97, 0.2)';
            }}
          />
        </div>
        
        <h1 style={titleStyle}>Benvenuto</h1>
        <p style={subtitleStyle}>Accedi al tuo account per continuare</p>
        
        <form onSubmit={handleLogin}>
          <div style={inputContainerStyle}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setFocusedInput('username')}
              onBlur={() => setFocusedInput(null)}
              required
              style={{
                ...inputStyle,
                ...(focusedInput === 'username' ? inputFocusStyle : {})
              }}
            />
            <style>{`
              input::placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
                opacity: 1;
              }
              input::-webkit-input-placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
              }
              input::-moz-placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
                opacity: 1;
              }
              input:-ms-input-placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
              }
            `}</style>
          </div>
          
          <div style={inputContainerStyle}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedInput('password')}
              onBlur={() => setFocusedInput(null)}
              required
              style={{
                ...inputStyle,
                paddingRight: '50px',
                ...(focusedInput === 'password' ? inputFocusStyle : {})
              }}
            />
            <div
              style={passwordToggleStyle}
              onClick={() => setShowPassword(!showPassword)}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ebcd61';
                e.currentTarget.style.backgroundColor = 'rgba(235, 205, 97, 0.15)';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(235, 205, 97, 0.6)';
                e.currentTarget.style.backgroundColor = 'rgba(235, 205, 97, 0.05)';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
              title={showPassword ? "Nascondi password" : "Mostra password"}
            >
              {showPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            style={buttonStyle}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-3px) scale(1.02)';
                e.target.style.boxShadow = '0 8px 28px rgba(235, 205, 97, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 4px 16px rgba(235, 205, 97, 0.3)';
              }
            }}
            onMouseDown={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px) scale(0.98)';
              }
            }}
            onMouseUp={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-3px) scale(1.02)';
              }
            }}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #1b1b1b',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  display: 'inline-block'
                }}></span>
                Accesso in corso...
              </span>
            ) : 'Accedi'}
          </button>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </form>

        {message && (
          <div style={errorMessageStyle}>
            {message}
          </div>
        )}
        </div>
      </div>
    </>
  );
}

export default Login;

